/**
 * miniAppFormula — safe arithmetic for Mini-Apps (no eval, no property access).
 *
 * Mirror of functions/helpers/miniAppFormula.js; functions/helpers/miniAppFormula.test.js
 * fails when the two drift. Edit both together.
 */

const MAX_FORMULA_CHARS = 400;
const MAX_NODES = 200;
const MAX_DEPTH = 40;
const NUMBER_LIMIT = 1e15;
const ID_PATTERN = /^[a-zA-Z][a-zA-Z0-9_]{0,39}$/;

const FUNCTIONS = {
  min: { arity: [1, 12], fn: (...a) => Math.min(...a) },
  max: { arity: [1, 12], fn: (...a) => Math.max(...a) },
  round: {
    arity: [1, 2],
    fn: (x, d = 0) => {
      const p = 10 ** Math.max(0, Math.min(6, Math.trunc(d)));
      return Math.round(x * p) / p;
    },
  },
  floor: { arity: [1, 1], fn: Math.floor },
  ceil: { arity: [1, 1], fn: Math.ceil },
  abs: { arity: [1, 1], fn: Math.abs },
  sqrt: { arity: [1, 1], fn: Math.sqrt },
  pow: { arity: [2, 2], fn: (a, b) => a ** b },
  clamp: { arity: [3, 3], fn: (x, lo, hi) => Math.min(Math.max(x, lo), hi) },
  if: { arity: [3, 3], fn: (c, a, b) => (c ? a : b) },
  and: { arity: [2, 12], fn: (...a) => (a.every(Boolean) ? 1 : 0) },
  or: { arity: [2, 12], fn: (...a) => (a.some(Boolean) ? 1 : 0) },
  not: { arity: [1, 1], fn: (a) => (a ? 0 : 1) },
};

const RESERVED = new Set(Object.keys(FUNCTIONS));

class FormulaError extends Error {}

const TWO_CHAR_OPS = ['<=', '>=', '==', '!=', '&&', '||'];
const ONE_CHAR_OPS = '+-*/%^(),<>!';
const NUMBER_RE = /^(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?/;
const IDENT_RE = /^[a-zA-Z_][a-zA-Z0-9_]*/;

function tokenize(src) {
  const tokens = [];
  let i = 0;
  while (i < src.length) {
    const c = src[i];
    if (c === ' ' || c === '\t' || c === '\n' || c === '\r') {
      i += 1;
      continue;
    }
    const rest = src.slice(i);
    const num = NUMBER_RE.exec(rest);
    if (num) {
      tokens.push({ t: 'num', v: Number(num[0]) });
      i += num[0].length;
      continue;
    }
    const id = IDENT_RE.exec(rest);
    if (id) {
      tokens.push({ t: 'id', v: id[0] });
      i += id[0].length;
      continue;
    }
    const two = src.slice(i, i + 2);
    if (TWO_CHAR_OPS.includes(two)) {
      tokens.push({ t: 'op', v: two });
      i += 2;
      continue;
    }
    if (ONE_CHAR_OPS.includes(c)) {
      tokens.push({ t: 'op', v: c });
      i += 1;
      continue;
    }
    throw new FormulaError(`Unexpected character "${c}"`);
  }
  return tokens;
}

const BINARY_LEVELS = [
  ['||'],
  ['&&'],
  ['==', '!=', '<', '<=', '>', '>='],
  ['+', '-'],
  ['*', '/', '%'],
];

function parse(tokens) {
  let pos = 0;
  let nodes = 0;

  const peek = () => tokens[pos];
  const isOp = (v) => peek() && peek().t === 'op' && peek().v === v;
  const node = (n, depth) => {
    nodes += 1;
    if (nodes > MAX_NODES) throw new FormulaError('Formula is too long');
    if (depth > MAX_DEPTH) throw new FormulaError('Formula is nested too deeply');
    return n;
  };
  const expect = (v) => {
    if (!isOp(v)) throw new FormulaError(`Expected "${v}"`);
    pos += 1;
  };

  const binary = (level, depth) => {
    if (level >= BINARY_LEVELS.length) return unary(depth);
    let left = binary(level + 1, depth + 1);
    while (peek() && peek().t === 'op' && BINARY_LEVELS[level].includes(peek().v)) {
      const op = tokens[pos].v;
      pos += 1;
      const right = binary(level + 1, depth + 1);
      left = node({ k: 'bin', op, a: left, b: right }, depth);
    }
    return left;
  };

  const unary = (depth) => {
    if (isOp('-') || isOp('+') || isOp('!')) {
      const op = tokens[pos].v;
      pos += 1;
      return node({ k: 'un', op, a: unary(depth + 1) }, depth);
    }
    return power(depth);
  };

  const power = (depth) => {
    const base = primary(depth + 1);
    if (isOp('^')) {
      pos += 1;
      return node({ k: 'bin', op: '^', a: base, b: unary(depth + 1) }, depth);
    }
    return base;
  };

  const primary = (depth) => {
    const tok = peek();
    if (!tok) throw new FormulaError('Formula ends unexpectedly');
    if (tok.t === 'num') {
      pos += 1;
      return node({ k: 'num', v: tok.v }, depth);
    }
    if (tok.t === 'id') {
      pos += 1;
      if (isOp('(')) {
        const def = FUNCTIONS[tok.v];
        if (!def) throw new FormulaError(`Unknown function "${tok.v}"`);
        pos += 1;
        const args = [];
        if (!isOp(')')) {
          args.push(binary(0, depth + 1));
          while (isOp(',')) {
            pos += 1;
            args.push(binary(0, depth + 1));
          }
        }
        expect(')');
        if (args.length < def.arity[0] || args.length > def.arity[1]) {
          throw new FormulaError(`Wrong number of arguments for "${tok.v}"`);
        }
        return node({ k: 'call', f: tok.v, args }, depth);
      }
      if (RESERVED.has(tok.v)) throw new FormulaError(`"${tok.v}" is a function, not a value`);
      return node({ k: 'var', n: tok.v }, depth);
    }
    if (isOp('(')) {
      pos += 1;
      const inner = binary(0, depth + 1);
      expect(')');
      return inner;
    }
    throw new FormulaError(`Unexpected "${tok.v}"`);
  };

  const ast = binary(0, 0);
  if (pos < tokens.length) throw new FormulaError(`Unexpected "${tokens[pos].v}"`);
  return ast;
}

function collectVars(ast, out = new Set()) {
  if (!ast) return out;
  if (ast.k === 'var') out.add(ast.n);
  if (ast.a) collectVars(ast.a, out);
  if (ast.b) collectVars(ast.b, out);
  if (ast.args) ast.args.forEach((arg) => collectVars(arg, out));
  return out;
}

/** Parse a formula. Throws FormulaError with a human-readable message. */
function compileFormula(src) {
  const text = typeof src === 'string' ? src.trim() : '';
  if (!text) throw new FormulaError('Formula is empty');
  if (text.length > MAX_FORMULA_CHARS) throw new FormulaError('Formula is too long');
  const ast = parse(tokenize(text));
  return { ast, vars: collectVars(ast) };
}

const finite = (n) => (Number.isFinite(n) && Math.abs(n) <= NUMBER_LIMIT ? n : NaN);

function evalNode(n, scope) {
  switch (n.k) {
    case 'num':
      return n.v;
    case 'var':
      return scope.has(n.n) ? Number(scope.get(n.n)) : NaN;
    case 'un': {
      const a = evalNode(n.a, scope);
      if (n.op === '-') return -a;
      if (n.op === '!') return a ? 0 : 1;
      return a;
    }
    case 'bin': {
      const a = evalNode(n.a, scope);
      const b = evalNode(n.b, scope);
      switch (n.op) {
        case '+': return a + b;
        case '-': return a - b;
        case '*': return a * b;
        case '/': return b === 0 ? NaN : a / b;
        case '%': return b === 0 ? NaN : a % b;
        case '^': return a ** b;
        case '<': return a < b ? 1 : 0;
        case '<=': return a <= b ? 1 : 0;
        case '>': return a > b ? 1 : 0;
        case '>=': return a >= b ? 1 : 0;
        case '==': return a === b ? 1 : 0;
        case '!=': return a !== b ? 1 : 0;
        case '&&': return a && b ? 1 : 0;
        case '||': return a || b ? 1 : 0;
        default: return NaN;
      }
    }
    case 'call':
      return FUNCTIONS[n.f].fn(...n.args.map((arg) => evalNode(arg, scope)));
    default:
      return NaN;
  }
}

/** Evaluate a compiled formula. Returns a finite number or null. */
function evaluateFormula(compiled, scope) {
  try {
    const value = finite(evalNode(compiled.ast, scope));
    return Number.isNaN(value) ? null : value;
  } catch (_) {
    return null;
  }
}

/**
 * Compute every output and chart bar of a Mini-App spec for the given input
 * values (id -> number). Missing / invalid inputs fall back to the default.
 * Outputs may reference inputs and earlier outputs.
 */
function evaluateMiniApp(spec, values = {}) {
  const scope = new Map();
  const inputs = Array.isArray(spec && spec.inputs) ? spec.inputs : [];
  inputs.forEach((input) => {
    const raw = values && Object.prototype.hasOwnProperty.call(values, input.id) ? Number(values[input.id]) : NaN;
    scope.set(input.id, Number.isFinite(raw) ? raw : Number(input.default) || 0);
  });

  const outputs = {};
  (Array.isArray(spec && spec.outputs) ? spec.outputs : []).forEach((output) => {
    let value = null;
    try {
      value = evaluateFormula(compileFormula(output.formula), scope);
    } catch (_) {
      value = null;
    }
    outputs[output.id] = value;
    scope.set(output.id, value == null ? NaN : value);
  });

  const bars = (spec && spec.chart && Array.isArray(spec.chart.bars) ? spec.chart.bars : []).map((bar) => {
    try {
      return evaluateFormula(compileFormula(bar.formula), scope);
    } catch (_) {
      return null;
    }
  });

  return { outputs, bars };
}

export {
  compileFormula,
  evaluateFormula,
  evaluateMiniApp,
  FormulaError,
  FUNCTIONS,
  RESERVED,
  ID_PATTERN,
  MAX_FORMULA_CHARS,
};
